'use client'

import dynamic from 'next/dynamic'
import { useSyncExternalStore } from 'react'
import type { SkyPresetName } from '@/components/marketing/home/prompt-showcase/golden-hour-sky'

/**
 * The homepage's FeralUI sky behind a dialog hero or an app card (the
 * integrations preview banner). The shader module loads
 * only when a hero first renders, so product routes don't carry it in
 * their first-load JS; until then (and wherever WebGL is missing) the
 * preset's own gradient is the background.
 */

/** Each preset's fallback stops, as in `SKY_PRESETS` (kept in sync by a test). */
export const DIALOG_SKY_STOPS = {
  goldenHour: ['#FFF3D6', '#FFD9A0', '#FFAE66', '#E07A52'],
  marigold: ['#FFEDDE', '#FFB342', '#FFAE66', '#FFC88A'],
  apricot: ['#FFC089', '#FFD9A0', '#FFAE66', '#FFC574'],
  marigoldLate: ['#FFEDDE', '#FFB342', '#FFAE66', '#FFC88A'],
} as const satisfies Record<SkyPresetName, ReadonlyArray<string>>

const GoldenHourSky = dynamic(
  () =>
    import('@/components/marketing/home/prompt-showcase/golden-hour-sky').then(
      (module) => module.GoldenHourSky
    ),
  { ssr: false }
)

/** Whether this runtime can draw WebGL; it never changes after load. */
function subscribeToNothing() {
  return () => undefined
}

function hasWebGl() {
  return typeof WebGLRenderingContext !== 'undefined'
}

/**
 * The server and the hydrating render both say no, so the markup matches;
 * the browser's render right after hydration says yes and mounts the shader.
 */
function serverHasWebGl() {
  return false
}

export function DialogSky({ preset }: { preset: SkyPresetName }) {
  const canDrawSky = useSyncExternalStore(
    subscribeToNothing,
    hasWebGl,
    serverHasWebGl
  )
  return (
    <div
      aria-hidden="true"
      data-slot="dialog-sky"
      data-sky={preset}
      className="pointer-events-none absolute inset-0 -z-10"
      style={{
        background: `linear-gradient(180deg, ${DIALOG_SKY_STOPS[preset].join(', ')})`,
      }}
    >
      {/* jsdom and other runtimes without WebGL keep the gradient alone. */}
      {canDrawSky ? <GoldenHourSky preset={preset} /> : null}
    </div>
  )
}
